<template>
  <PageLayout
    data-qa="DASHBOARD_PAGE"
    data-qa-menu-sandwich-close="DASHBOARD_MENU_TOGGLE_CLOSE_BTN"
    data-qa-menu-sandwich-open="DASHBOARD_MENU_TOGGLE_OPEN_BTN"
  >
    <template #navigation-bar>
      <NavigationBar
        :items="navigationItems"
        data-qa="DASHBOARD_LOGO_IMG"
        :path="route.path"
        :version="nuxtRuntimeConfig.public.appVersion"
      />
    </template>
    <template #breadcrumb>
      <BreadcrumbNav :links="breadcrumbs" />
    </template>
    <template #language-menu>
      <LanguageMenu v-model="uiLocale" :options="languages" data-qa="LOGIN_LANGUAGE" />
    </template>
    <template #user-menu>
      <UserMenu
        :options="userMenuOptions"
        :key="localeVersions"
        theme="success"
        :avatar-text="avatarText"
        :title="username"
        :subtitles="[roleName]"
        @click:sign-out="signOut"
        class="user-menu"
      />
    </template>
    <template #content>
      <slot></slot>
    </template>
  </PageLayout>
</template>

<script setup lang="ts">
import {
  PageLayout,
  UserMenu,
  BreadcrumbNav,
  NavigationBar,
  LanguageMenu
} from '@lw-design-system/ui';
import { useBreadcrumbItems } from '~/composables/useBreadcrumbItems';
import { navigationItems as rawNavigationItems } from '@/utils/navigation-items';
import { findInNestedByName } from '@/utils/breadcrumbs';
import { signOut, triggerJimoChangelog } from '@/utils/auth';
import { useWalletsStore } from '~/stores/wallets/wallets-store';
import { useWallets } from '~/composables/wallets/useWallets';
import { useBranding } from '~/composables/branding/useBranding';
import { useBrandingStore } from '~/stores/branding/branding-store';
import type { NavigationItem } from '~/types';

const route = useRoute();
const runtimeConfig = useConfig();
const nuxtRuntimeConfig = useRuntimeConfig();
const i18n = useI18n();
const { locale, locales, t, getLocaleCookie, setLocale, setLocaleCookie } = i18n;
const store = useStore();
const { localeVersions, pageBreadcrumb } = storeToRefs(store);
const appliedLocale = ref<string>(locale.value);
const uiLocale = ref<string>(locale.value);
const { userInfos, tabs, selectedTab, activePMTab } = useWallets();
const { walletIntId } = storeToRefs(useWalletsStore());
const { transactionInfos, transactionP2PInfos, activeTab } = storeToRefs(useTransactionsStore());
const { transactionDetailsTabs } = useTransactions();
const { brandingTabs } = useBranding();

const languages = computed(() =>
  locales.value.map((language) => ({ ...language, dataQa: language.name?.toUpperCase() }))
);

const headwayClickHandler = ref<((evt: MouseEvent) => void) | null>(null);

const initHeadway = () => {
  const headway = window.Headway;
  if (!headway?.init) return;

  if (headwayClickHandler.value) {
    document.removeEventListener('click', headwayClickHandler.value, true);
    headwayClickHandler.value = null;
  }

  headway.init({
    account: runtimeConfig.value.public.headwayId as string,
    selector: '.changelog-badge',
    trigger: '.changelog-trigger',
    translations: {
      title: t('common.latestUpdates'),
      footer: t('common.viewFullChangelog')
    },
    position: { version: 'v8', y: 'center' },
    callbacks: {
      onWidgetReady: (widget) => {
        headwayClickHandler.value = () => widget.hide();
        document.addEventListener('click', headwayClickHandler.value, true);
      }
    }
  });
};

onBeforeMount(() => {
  triggerJimoChangelog(true);
});

onMounted(async () => {
  const cookie = getLocaleCookie();
  if (cookie && isSupportedLocale(cookie) && cookie !== locale.value) {
    await setLocale(cookie);
    setLocaleCookie(cookie);
    appliedLocale.value = cookie;
  } else {
    appliedLocale.value = locale.value;
  }

  uiLocale.value = appliedLocale.value;

  reloadDevextremeMessages(appliedLocale.value);

  initHeadway();

  if (runtimeConfig.value.public.lemonwayEnv.toLowerCase() === 'production') {
    window.wootricSettings = {
      account_token: runtimeConfig.value.public.wootricId,
      modal_footprint: 'compact',
      email: localStorage.getItem('email') ?? '',
      created_at: localStorage.getItem('userCreatedAt')
        ? getDate(localStorage.getItem('userCreatedAt')).unix().toString()
        : '',
      language: locale.value,
      modal_theme: 'light',
      properties: {
        env: runtimeConfig.value.public.lemonwayEnv,
        partnerName: route.params.partner.toString(),
        partnerActivity: localStorage.getItem('partnerActivity') ?? '',
        partnerCountry: localStorage.getItem('partnerCountry') ?? '',
        username: localStorage.getItem('username') ?? '',
        role: localStorage.getItem('roleName') ?? '',
        language: locale.value
      }
    };

    window.WootricSurvey.run();
  }
});

watch(locale, (newLocale: string) => {
  if (uiLocale.value !== newLocale) uiLocale.value = newLocale;
});

watch(uiLocale, async (selectedLocale: string) => {
  if (!isSupportedLocale(selectedLocale)) return;
  if (selectedLocale === appliedLocale.value) return;

  await setLocale(selectedLocale);
  setLocaleCookie(selectedLocale);
  reloadDevextremeMessages(selectedLocale);
  store.bumpLocaleVersions();

  appliedLocale.value = selectedLocale;

  await nextTick();
  initHeadway();

  localStorage.setItem('userLocale', selectedLocale);
});

onBeforeUnmount(() => {
  if (headwayClickHandler.value) {
    document.removeEventListener('click', headwayClickHandler.value, true);
    headwayClickHandler.value = null;
  }
});

const username = ref(localStorage.getItem('username'));
const roleName = ref(localStorage.getItem('roleName'));
const avatarText = computed(() => {
  if (!username.value) {
    return '';
  }
  return username.value
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
});
const filtredRawNavigationItems = computed(() => {
  return rawNavigationItems.filter(function deepFilter(item): boolean {
    let permission = item.permission;

    if (permission && item.options) {
      permission = (item.options = item.options.filter(deepFilter)).length > 0;
    }

    return permission;
  });
});

const currentPartner = computed(() => (route.params?.partner ? String(route.params.partner) : ''));

const navigationItems = shallowRef<NavigationItem[]>([]);

const buildNavigationItems = (partner: string) => {
  return filtredRawNavigationItems.value.map((item) => {
    return {
      ...item,
      text: item.text ? t(item.text) : '',
      url:
        item.type !== 'EXTERNAL_LINK'
          ? item.url !== '#'
            ? `/${partner}/${item.url}`
            : item.url
          : item.url,
      options: item.options?.map((option) => {
        if ('text' in option) {
          return {
            ...option,
            text: option.text ? t(option.text) : '',
            url: option.type !== 'EXTERNAL_LINK' ? `/${partner}/${option.url}` : option.url
          };
        } else {
          return option;
        }
      })
    };
  });
};

watch(
  [currentPartner, appliedLocale],
  ([partner]) => {
    if (!partner) return;
    navigationItems.value = buildNavigationItems(partner);
  },
  { immediate: true }
);

const userMenuOptions = computed(() => [
  { code: undefined, name: undefined },
  {
    code: 'AccountCircle',
    name: t('navigation.profileSettings'),
    url: `/${route.params?.partner}/profile-settings`
  },
  { code: 'Logout', name: t('common.signOut') }
]);

const breadcrumbsItems = useBreadcrumbItems();

const breadcrumbs = computed(() => {
  if (route.name === '404') {
    return;
  }
  const breadcrumbs = findInNestedByName(breadcrumbsItems.value, route.name as string);

  const lemonwayEnv =
    runtimeConfig.value.public.lemonwayEnv.toLowerCase() === 'production'
      ? ''
      : ` (${runtimeConfig.value.public.lemonwayEnv})`;

  breadcrumbs?.unshift({
    to: { name: 'partner-home' },
    label: `${route.params?.partner}${lemonwayEnv}`
  });

  if (walletIntId.value) {
    const walletName = computed(() =>
      userInfos.value.isCompany
        ? userInfos.value.companyName
        : `${userInfos.value.clientFirstName} ${userInfos.value.clientLastName}`
    );
    breadcrumbs[2] = {
      ...breadcrumbs[2],
      to: {
        name: 'partner-wallets-id-tab-pmtab',
        params: { tab: 'overview', id: walletIntId.value || 0, pmtab: activePMTab.value }
      },
      label: walletName.value
    };
    breadcrumbs.push({
      to: {
        name: 'partner-wallets-id-tab-pmtab',
        params: {
          tab: selectedTab.value || 'overview',
          id: walletIntId.value || 0,
          pmtab: activePMTab.value
        }
      },
      label: tabs.value.find((tab) => tab.value === selectedTab.value)?.label
    });
  }

  if (transactionInfos.value && !transactionP2PInfos.value) {
    breadcrumbs.push({
      name: 'partner-transactions-id-tab',
      to: {
        name: 'partner-transactions-id-tab',
        params: { id: transactionInfos.value.id, tab: activeTab.value }
      },
      label: transactionDetailsTabs.value
        .find((tab) => tab.value === activeTab.value)
        ?.label.replace(/\s\(\d+\)$/, '')
    });
  }

  if (transactionP2PInfos.value) {
    breadcrumbs.push({
      name: 'partner-transactions-p2p-id-tab',
      to: {
        name: 'partner-transactions-p2p-id-tab',
        params: { id: transactionP2PInfos.value.id, tab: activeTab.value }
      },
      label: transactionDetailsTabs.value
        .find((tab) => tab.value === activeTab.value)
        ?.label.replace(/\s\(\d+\)$/, '')
    });
  }

  if (route.name === 'partner-branding-brand-tab') {
    breadcrumbs.push({
      name: 'partner-branding-brand-tab',
      to: {
        name: 'partner-branding-brand-tab',
        params: { tab: storeToRefs(useBrandingStore()).activeTab.value }
      },
      label: brandingTabs.value.find(
        (tab) => tab.value === storeToRefs(useBrandingStore()).activeTab.value
      )?.label
    });
  }

  if (pageBreadcrumb.value) {
    breadcrumbs.push(pageBreadcrumb.value);
  }

  return breadcrumbs;
});
</script>

<style lang="css">
#HW_badge_cont {
  position: absolute;
  top: calc(40% - 12px);
  right: 5.625rem;
}

.navigation-item {
  position: relative;
}

.user-menu {
  cursor: pointer;
}
</style>
